import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';
import { inputCls } from './shared';

/**
 * Who sat on the interview panel.
 *
 * A list of the organisation's own staff rather than a free-text box: the panel
 * is almost always people already in ServePatch, and typing their names by hand
 * produced "Steven", "Steve R" and "S. Rowley" for one person across three
 * candidates — which makes "who interviewed her?" unanswerable a year later.
 *
 * Still not a closed list. A name already stored that nobody matches — an
 * external panellist, somebody who has since left — stays selected and
 * selectable, because deleting it from the record to tidy a dropdown would be
 * rewriting history.
 *
 * Names are stored, not ids: the record has to keep reading correctly after
 * somebody leaves and their row is archived.
 *
 * @param {string[]} value      the names currently on the panel
 * @param {Array}    staff      [{ id, name }] to choose from
 * @param {Function} onChange   called with the new list of names
 */
const InterviewPanelPicker = ({ value = [], staff = [], onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close on a click anywhere else. A dropdown that only closes by reselecting
  // it covers the fields underneath.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = Array.isArray(value) ? value : [];
  const staffNames = staff.map((person) => person.name).filter(Boolean);
  // Anyone already on the panel who is not in the staff list keeps their place.
  const options = [...staffNames, ...selected.filter((name) => !staffNames.includes(name))];

  const toggle = (name) => onChange(
    selected.includes(name) ? selected.filter((row) => row !== name) : [...selected, name],
  );

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`${inputCls} flex items-center justify-between gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        <span className={`truncate ${selected.length ? 'text-[#0B1220] dark:text-gray-100' : 'text-[#94A3B8] dark:text-gray-500'}`}>
          {selected.length ? `${selected.length} selected` : 'Select interviewers'}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* The chosen names, readable without opening the list — a count alone
          means reopening the dropdown to remember who is on it. */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-[#EFF6FF] dark:bg-blue-900/20 text-[12.5px] font-medium text-[#1D4ED8] dark:text-blue-300"
            >
              {name}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  onClick={() => toggle(name)}
                  className="text-[#93B4E8] hover:text-[#1D4ED8]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] shadow-lg py-1">
          {options.length === 0 ? (
            <p className="px-3.5 py-2.5 text-[13px] text-[#94A3B8] dark:text-gray-500">
              No staff to choose from yet.
            </p>
          ) : options.map((name) => {
            const on = selected.includes(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggle(name)}
                className="w-full flex items-center justify-between gap-2 px-3.5 py-2 text-left text-[13px] text-[#1E293B] dark:text-gray-200 hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b]"
              >
                <span className="truncate">{name}</span>
                {on && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InterviewPanelPicker;
